import { getOverview } from '../api/endpoints';
import { useApi } from '../api/hooks';
import type { DealBreakdown, OpenLoan, Overview as OverviewData, Receivable } from '../api/model';
import { Async, Notice } from '../components/Async';
import { CardHead, Kpi } from '../components/Card';
import { EventsTable } from '../components/EventsTable';
import { HelpLabel } from '../components/Help';
import { PriceSourceLabel } from '../components/PriceSource';
import { PrehistoryCreditNote } from '../components/PrehistoryCreditNote';
import { Table, type Column } from '../components/Table';
import { Dash, Val } from '../components/Val';
import { reasonText, valuationText } from '../i18n/reasons.hu';
import { formatDecimal, formatPercent, isZeroDecimal } from '../lib/decimal';
import { Link } from '../lib/router';
import { formatUtc } from '../lib/time';

const SEG_CLASS = ['c1', 'c2', 'c3', 'c4'];

function priceKindLabel(kind: OpenLoan['entry_price_kind'], coin: string | null): string {
  if (kind === 'sell_average') return '(eladási átlag)';
  if (kind === 'borrow_candle') return '(felvételi gyertya)';
  if (kind === 'reference_candle') return `(referencia-coin${coin ? `: ${coin}` : ''})`;
  return '';
}

function openLoanColumns(): Column<OpenLoan>[] {
  return [
    { key: 'asset', label: 'Hitel', help: 'loanAsset', ariaLabel: 'hitel', sortValue: (l) => l.asset, render: (l) => <b>{l.asset}</b> },
    { key: 'opened', label: 'Felvéve (UTC)', help: 'openedAt', ariaLabel: 'felvétel ideje', sortValue: (l) => l.opened_at, render: (l) => (l.opened_at ? <span className="mono">{formatUtc(l.opened_at)}</span> : <Dash reason="no_data" />) },
    { key: 'qty', label: 'Mennyiség', numeric: true, help: 'quantity', ariaLabel: 'mennyiség', sortValue: (l) => l.quantity, render: (l) => <span className="mono">{formatDecimal(l.quantity)} {l.asset}</span> },
    {
      key: 'entry', label: 'Felvételi ár', numeric: true, help: 'entryPrice', ariaLabel: 'felvételi ár', sortValue: (l) => l.entry_price,
      render: (l) => (
        <span className="mono">
          <Val value={l.entry_price} reason={l.entry_price_reason} dp={2} />
          {l.entry_price !== null ? <span className="muted"> {l.entry_price_asset ?? ''} {priceKindLabel(l.entry_price_kind, l.price_asset)}</span> : null}
        </span>
      ),
    },
    { key: 'candle', label: 'Gyertya a felvételkor', numeric: true, help: 'candlePrice', ariaLabel: 'gyertya-ár', sortValue: (l) => l.candle_price, render: (l) => <span className="mono muted"><Val value={l.candle_price} reason={l.candle_price_reason} maxDp={4} /></span> },
    {
      key: 'today', label: 'Mai ár', numeric: true, help: 'currentPrice', ariaLabel: 'mai ár', sortValue: (l) => l.current_price,
      render: (l) => (
        <span className="mono">
          <Val value={l.current_price} reason={l.current_price_reason} dp={2} />
          {l.current_price !== null && l.current_price_asset ? <span className="muted"> {l.current_price_asset}{l.price_asset ? ` / ${l.price_asset}` : ''}</span> : null}
          {l.current_price !== null ? <PriceSourceLabel source={l.current_price_source} /> : null}
        </span>
      ),
    },
    {
      key: 'dev', label: 'Eltérés', numeric: true, help: 'deviation', ariaLabel: 'eltérés', sortValue: (l) => l.deviation_pct,
      render: (l) => (l.deviation_pct === null ? <Dash reason={l.deviation_reason} /> : <span className="mono warn">{formatPercent(l.deviation_pct)}</span>),
    },
    {
      key: 'out', label: 'Kint lévő', numeric: true, help: 'outstanding', ariaLabel: 'kint lévő', sortValue: (l) => l.outside_amount,
      render: (l) => (l.outside_amount === null ? <Dash reason="no_data" /> : <span className={`mono ${!isZeroDecimal(l.outside_amount) ? 'warn' : ''}`}>{formatDecimal(l.outside_amount)} {l.asset}</span>),
    },
    { key: 'deal', label: 'Ügylet', help: 'deal', ariaLabel: 'ügylet', sortValue: (l) => l.deal_id, render: (l) => (l.deal_id === null ? <span className="muted">—</span> : <Link to={`/deals/${l.deal_id}`}>#{l.deal_id} {l.deal_name}</Link>) },
  ];
}

/** D49: which source priced each coin (Candle -> Binance public -> manual); stablecoins are valued 1:1. */
function ValuationSources({ v }: { v: OverviewData['valuation'] }) {
  const entries = Object.entries(v.price_sources).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0 && !v.stablecoin_parity) return null;
  return (
    <div className="muted small" data-testid="valuation-sources">
      Árforrás:{' '}
      {entries.map(([asset, src]) => <span key={asset} style={{ marginRight: 10 }}>{asset}<PriceSourceLabel source={src} /></span>)}
      {v.stablecoin_parity ? <span>USDT, USDC<PriceSourceLabel source="stablecoin_parity" /></span> : null}
    </div>
  );
}

function BreakdownBlock({ b }: { b: DealBreakdown }) {
  const aria = b.segments.map((s) => `${s.location} ${s.assets}${s.share_pct ? ` ${formatDecimal(s.share_pct, { maxDp: 1 })}%` : ''}`).join(', ');
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="card-head">
        <Link to={`/deals/${b.deal_id}`} style={{ fontWeight: 600 }}>
          #{b.deal_id} {b.deal_name}
        </Link>
        <span className="mono muted">{b.headline}</span>
      </div>
      <div className="stackbar" role="img" aria-label={aria}>
        {b.segments.map((s, i) => (
          <div key={`${s.location}-${s.assets}-${i}`} className={SEG_CLASS[i % 4]} style={{ width: `${Math.max(0, Math.min(100, Number(s.share_pct ?? 0)))}%` }} />
        ))}
      </div>
      <div className="legend">
        {b.segments.map((s, i) => (
          <span key={`${s.location}-${s.assets}-${i}`}>
            <span className={`swatch ${SEG_CLASS[i % 4]}`} />
            {s.location} · {s.assets} · <span className="mono"><Val value={s.value_usd} reason={s.value_reason} dp={0} /></span>
          </span>
        ))}
      </div>
    </div>
  );
}

function ReceivableCard({ r }: { r: Receivable }) {
  return (
    <div className="recv">
      <div className="line">
        <span>{r.location}</span>
        <span className="mono">{formatDecimal(r.outstanding)} {r.asset}</span>
      </div>
      <div className="small muted">
        #{r.deal_id} {r.deal_name}
        {r.sent_at ? ` · kiutalva ${formatUtc(r.sent_at)} UTC · ${r.days_out} napja kint` : ''}
      </div>
      <div className="small">
        Fedezetlen:{' '}
        <b className={`mono ${r.uncovered !== null && r.uncovered !== '0' ? 'neg' : ''}`}>
          <Val value={r.uncovered} reason={r.uncovered_reason} unit={r.asset} dp={2} />
        </b>
      </div>
    </div>
  );
}

function OverviewBody({ d }: { d: OverviewData }) {
  const k = d.kpis;
  const loanSummary = d.open_loans.map((l) => `${formatDecimal(l.quantity)} ${l.asset}`).join(' + ');
  return (
    <>
      <section aria-label="Fő mutatók" className="kpis">
        <Kpi helpKey="openLoan" label="Nyitott kézi hitel" value={k.open_loan_usd.value} reason={k.open_loan_usd.reason} unit="USD" caption={`${k.open_loan_count} hitel${loanSummary ? ` · ${loanSummary}` : ''}`} dp={0} />
        <Kpi helpKey="outsideMargin" label="Binance Marginon kívül" value={k.outside_binance_margin_usd.value} reason={k.outside_binance_margin_usd.reason} unit="USD" tone="warn" caption={k.outside_locations.length ? `${k.outside_locations.length} helyszínen · ${k.outside_locations.join(', ')}` : 'nincs kint lévő összeg'} dp={0} />
        <Kpi helpKey="interest" label="Számolt kamat (nyitott)" value={k.calculated_interest_usd.value} reason={k.calculated_interest_usd.reason} unit="USD" caption="számolt · nem tőzsdei rekord" />
        <Kpi helpKey="realized" label="Realizált eredmény" value={k.realized_result_usd.value} reason={k.realized_result_usd.reason} unit="USD" tone="result" sign caption={`${k.closed_deal_count} lezárt ügylet`} />
      </section>

      <PrehistoryCreditNote credits={k.open_loan_prehistory_credit} />

      {d.interest_gaps.missing_hours > 0 ? (
        <Notice kind="warn" title="A számolt kamat hiányos">
          {d.interest_gaps.missing_hours} órára még nincs kamat könyvelve. {reasonText(d.interest_gaps.reason)} Ezért a kamat-mutató alsó becslés: nem nulla és nem végleges.
        </Notice>
      ) : null}

      <section className="card tight" aria-label="Nyitott hitelek">
        <CardHead title="Nyitott hitelek" hint="Felvételi ár: coin-hitelnél a felvett coin eladási átlaga (D24), eladás nélkül a felvételkori gyertya-ár (D40, ügylet nélkül is); stablecoin-hitelnél az ügylet referencia-coinja (D10)">
          <Link to="/loans" className="btn small">Hitelenként →</Link>
        </CardHead>
        <Table columns={openLoanColumns()} rows={d.open_loans} rowKey={(l) => l.key} defaultSort={{ key: 'opened', dir: 'desc' }} label="Nyitott hitelek" empty="Nincs nyitott kézi hitel." />
      </section>

      <div className="cols">
        <section className="card grow" style={{ gap: 18 }} aria-label="Hol van a pénz">
          <div className="card-head">
            <HelpLabel helpKey="breakdown" label="hol van a hitelből származó pénz" className="" as="h2">
              Hol van a hitelből származó pénz
            </HelpLabel>
            <span className="muted small">mai érték, USD · eredet-címke szerint (D21)</span>
          </div>
          {d.deal_breakdown.length === 0 ? <div className="muted">Még nincs ügylet, amelyhez tartozó pénzt mutatni lehetne.</div> : d.deal_breakdown.map((b) => <BreakdownBlock key={b.deal_id} b={b} />)}
        </section>

        <section className="card side" aria-label="Visszahozandó">
          <HelpLabel helpKey="receivables" label="visszahozandó" className="" as="h2">
            Visszahozandó
          </HelpLabel>
          {d.receivables.length === 0 ? <div className="muted">Nincs visszahozandó összeg.</div> : d.receivables.map((r) => <ReceivableCard key={`${r.deal_id}-${r.location}-${r.asset}`} r={r} />)}
          <Link to="/locations" className="small">
            Minden helyszín egyenlege →
          </Link>
        </section>
      </div>

      <div className="muted small" data-testid="valuation-note">{valuationText(d.valuation)}</div>
      <ValuationSources v={d.valuation} />

      <section className="card tight" aria-label="Legutóbbi események">
        <div className="card-head">
          <HelpLabel helpKey="recentEvents" label="legutóbbi események" className="" as="h2">
            Legutóbbi események
          </HelpLabel>
          <span className="muted small">Forrás: Binance (megjelölt), kézi bevitel vagy számolt kamat</span>
          <Link to="/events" className="small" style={{ marginLeft: 'auto' }}>
            Mind →
          </Link>
        </div>
        <EventsTable events={d.recent_events} variant="recent" label="Legutóbbi események" />
      </section>
    </>
  );
}

export function OverviewPage() {
  const state = useApi(getOverview);
  return (
    <main className="page">
      <div className="page-head">
        <div className="titles">
          <h1>Áttekintés</h1>
          <div className="muted">Kézi margin-hitelek és ami belőlük lett · Binance Margin · minden időpont UTC</div>
        </div>
        <div className="row-wrap">
          <Link className="btn" to="/entries/new">
            Kézi bevitel
          </Link>
          <Link className="btn primary" to="/mark">
            Binance-események megjelölése
          </Link>
        </div>
      </div>
      <Async state={state}>{(d) => <OverviewBody d={d} />}</Async>
    </main>
  );
}
